import React, { useState, useEffect } from "react";
import UserHeader from "../components/userManagement/UserHeader";
import UserTable from "../components/userManagement/UserTable";
import { useDeleteUser } from "../hooks/useUsers"; // Import the delete user hook
import AddNewUserModal from "../components/userManagement/AddUserModal";
import { Alert } from "antd";
import { usePermissions } from "../context/PermissionContext";
import { PERMISSIONS } from "../utils/permissions";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../hooks/useAuth";

const UserManagementPage: React.FC = () => {
  const navigate = useNavigate();
  const { data: user } = useAuth();
  const [isModalVisible, setModalVisible] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);

  // Get location ID from logged in user
  const locationId = user?.location?.id;

  // Get user permissions
  const { hasPermission } = usePermissions();
  const canViewUsers = hasPermission(PERMISSIONS.VIEW_USERS);
  const canAddUsers = hasPermission(PERMISSIONS.ADD_USERS);
  const canModifyUsers = hasPermission(PERMISSIONS.MODIFY_USERS);
  const canDeleteUsers = hasPermission(PERMISSIONS.DELETE_USERS);
  const canSeeAllLocations = hasPermission(PERMISSIONS.CAN_SEE_ALL_LOCATIONS);

  // Initialize filters - only include location_id if user can't see all locations
  const [filters, setFilters] = useState<Record<string, any>>(() => {
    const initialFilters: Record<string, any> = {
      user_type_id: 2, // Staff user type ID
      include_guests: false,
    };
    if (!canSeeAllLocations && locationId) {
      initialFilters.location_id = locationId;
    }
    return initialFilters;
  });

  // Redirect if user doesn't have permission to view users
  useEffect(() => {
    if (!canViewUsers) {
      // Redirect to dashboard with a delay to prevent render issues
      const timeout = setTimeout(() => {
        navigate("/unauthorized");
      }, 100);

      return () => clearTimeout(timeout);
    }
  }, [canViewUsers, navigate]);

  // Handle Opening Modal
  const handleAddUser = () => {
    if (!canAddUsers) {
      return; // Don't open modal if user doesn't have permission
    }
    setSelectedUser(null);
    setModalVisible(true);
  };

  // Handle Editing User
  const handleModifyUser = (user: any) => {
    if (!canModifyUsers) {
      return; // Don't open modal if user doesn't have permission
    }
    setSelectedUser(user);
    setModalVisible(true);
  };

  // Handle Filters
  const handleApplyFilters = (newFilters: Record<string, any>) => {
    console.log("Applying filters:", newFilters);

    // Ensure user_type_id is a number if present
    if (newFilters.user_type_id !== undefined) {
      newFilters.user_type_id = Number(newFilters.user_type_id);
    }

    // Ensure include_guests is a boolean
    if (newFilters.include_guests !== undefined) {
      newFilters.include_guests =
        newFilters.include_guests === true ||
        newFilters.include_guests === "true";
    } else {
      // Default to false if not specified
      newFilters.include_guests = false;
    }

    // Ensure location filter is maintained for users who can't see all locations
    if (!canSeeAllLocations && locationId) {
      newFilters.location_id = locationId;
    }

    setFilters(newFilters);
  };

  const handleClearFilters = () => {
    // Reset to default filters but keep location for restricted users
    console.log("Clearing filters, setting to default");
    const clearedFilters: Record<string, any> = {
      include_guests: false,
    };
    if (!canSeeAllLocations && locationId) {
      clearedFilters.location_id = locationId;
    }
    setFilters(clearedFilters);
  };

  // Handle Deleting User
  const { mutate: deleteUser } = useDeleteUser();
  const handleDeleteUser = (userId: number) => {
    if (!canDeleteUsers) {
      return; // Don't delete if user doesn't have permission
    }
    deleteUser(userId);
  };

  // If user doesn't have permission to view users, show access denied message
  if (!canViewUsers) {
    return (
      <div className="p-6">
        <Alert
          message="Access Denied"
          description="You don't have permission to access user management. Please contact your administrator if you need access."
          type="error"
          showIcon
        />
      </div>
    );
  }

  return (
    <div className="p-6 bg-white min-h-screen">
      <UserHeader
        onAddUser={handleAddUser}
        onApplyFilters={handleApplyFilters}
        onClearFilters={handleClearFilters}
        canAddUsers={canAddUsers}
        canSeeAllLocations={canSeeAllLocations}
      />
      <UserTable
        onModifyUser={handleModifyUser}
        onDeleteUser={handleDeleteUser}
        filters={filters}
        canModifyUsers={canModifyUsers}
        canDeleteUsers={canDeleteUsers}
      />

      {/* Add or Modify User Modal */}
      <AddNewUserModal
        visible={isModalVisible}
        onCancel={() => setModalVisible(false)}
        onSubmit={handleAddUser}
        user={selectedUser}
      />
    </div>
  );
};

export default UserManagementPage;
